import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Settings } from 'lucide-react';
import axios from 'axios';

const BookingAmountSet = () => {
  const [settings, setSettings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [formData, setFormData] = useState({
    course_id: '',
    regular_amount: '',
    lateral_amount: '',
    description: '',
    session_id: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
    fetchCourses();
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/seat-booking/amount-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching amount settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate course
    if (!editingSetting) {
      const existingSetting = settings.find(s => s.course_id === formData.course_id);
      if (existingSetting) {
        alert(`Booking amount for this course is already set. Please edit the existing one or delete it first.`);
        return;
      }
    }
    
    setLoading(true);
    try {
      const selectedCourse = courses.find(c => c.id === formData.course_id);
      const submitData = {
        course_id: formData.course_id,
        regular_amount: parseFloat(formData.regular_amount) || 0,
        lateral_amount: selectedCourse?.lateral_entry_allowed ? (parseFloat(formData.lateral_amount) || 0) : (parseFloat(formData.regular_amount) || 0),
        amount: parseFloat(formData.regular_amount) || 0, // Keep for backward compatibility
        description: formData.description,
        session_id: formData.session_id
      };
      
      if (editingSetting) {
        await axios.put(`${API_URL}/api/seat-booking/amount-settings/${editingSetting.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Amount setting updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/seat-booking/amount-settings`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Amount setting created successfully!');
      }
      fetchSettings();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving amount setting:', error);
      alert(error.response?.data?.detail || 'Failed to save amount setting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this amount setting?')) {
      setLoading(true);
      try {
        await axios.delete(`${API_URL}/api/seat-booking/amount-settings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Amount setting deleted successfully!');
        fetchSettings();
      } catch (error) {
        console.error('Error deleting amount setting:', error);
        alert('Failed to delete amount setting. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setFormData({
      course_id: setting.course_id,
      regular_amount: setting.regular_amount || setting.amount || '',
      lateral_amount: setting.lateral_amount || setting.amount || '',
      description: setting.description || '',
      session_id: setting.session_id || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSetting(null);
    setFormData({ 
      course_id: '', 
      regular_amount: '', 
      lateral_amount: '',
      description: '', 
      session_id: ''
    });
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'N/A';
  };

  const filteredSettings = settings.filter(setting => {
    const courseName = getCourseName(setting.course_id);
    return courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           setting.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Amount Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure booking amounts for Regular & Lateral Entry students</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          disabled={loading}
        >
          <Plus className="h-4 w-4" />
          Add Amount Setting
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <Settings className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Set default booking amounts for each course. <strong>Regular Entry</strong> is for 1st semester students, 
              <strong> Lateral Entry</strong> is for students joining from higher semesters (e.g., 3rd semester).
            </p>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Total Course Settings</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{settings.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Avg Regular Amount</p>
            <p className="text-3xl font-bold text-blue-600 mt-1">
              ₹{settings.length > 0 ? Math.round(settings.reduce((sum, s) => sum + (s.regular_amount || s.amount || 0), 0) / settings.length).toLocaleString('en-IN') : 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Avg Lateral Amount</p>
            <p className="text-3xl font-bold text-orange-600 mt-1">
              ₹{settings.length > 0 ? Math.round(settings.reduce((sum, s) => sum + (s.lateral_amount || s.amount || 0), 0) / settings.length).toLocaleString('en-IN') : 0}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by course name or description..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lateral Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredSettings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm ? 'No matching settings found' : 'No amount settings configured'}
                  </td>
                </tr>
              ) : (
                filteredSettings.map((setting) => {
                  const course = courses.find(c => c.id === setting.course_id);
                  return (
                  <tr key={setting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{getCourseName(setting.course_id)}</div>
                      {course?.lateral_entry_allowed && (
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Lateral Entry Allowed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="bg-blue-50 px-3 py-1 rounded-full inline-block">
                        <span className="text-sm font-bold text-blue-700">₹{(setting.regular_amount || setting.amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {course?.lateral_entry_allowed ? (
                        <div className="bg-orange-50 px-3 py-1 rounded-full inline-block">
                          <span className="text-sm font-bold text-orange-700">₹{(setting.lateral_amount || setting.amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{setting.description || '-'}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(setting)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(setting.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingSetting ? 'Edit Amount Setting' : 'Add Amount Setting'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                <select
                  name="course_id"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.course_id}
                  onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                  disabled={editingSetting}
                >
                  <option value="">Select Course</option>
                  {courses.filter(course => {
                    // Check if course already has a booking amount setting
                    const existingForCourse = settings.find(s => 
                      s.course_id === course.id && s.id !== editingSetting?.id
                    );
                    return !existingForCourse;
                  }).map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name} ({course.code})
                      {course.lateral_entry_allowed ? ' - Lateral Entry Allowed' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Regular Booking Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Regular Entry Booking Amount (₹) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="100"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.regular_amount}
                    onChange={(e) => setFormData({ ...formData, regular_amount: e.target.value })}
                    placeholder="e.g., 20000"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">For students joining from 1st Semester</p>
              </div>

              {/* Lateral Booking Amount - Only show if course allows lateral entry */}
              {(() => {
                const selectedCourse = courses.find(c => c.id === formData.course_id);
                if (selectedCourse?.lateral_entry_allowed) {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lateral Entry Booking Amount (₹) *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                        <input
                          type="number"
                          required
                          min="0"
                          step="100"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={formData.lateral_amount}
                          onChange={(e) => setFormData({ ...formData, lateral_amount: e.target.value })}
                          placeholder="e.g., 15000"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        For students joining from {selectedCourse?.lateral_entry_semester || 3}rd Semester
                      </p>
                    </div>
                  );
                } else if (formData.course_id) {
                  return (
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-500">Lateral Entry not available for this course</p>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Session Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session (Optional)
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={formData.session_id}
                  onChange={(e) => setFormData({ ...formData, session_id: e.target.value })}
                  disabled={!formData.course_id}
                >
                  <option value="">All Sessions</option>
                  {sessions
                    .filter(s => s.is_active !== false && (!formData.course_id || s.course_id === formData.course_id))
                    .map(session => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingSetting ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingAmountSet;